import { HttpClient, HttpParams } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { AccountInfoRequest } from '../../models/interfaces/account-info.interface'
import { BasicFile } from '../../models/interfaces/basic-file.interface'
import { BasicFunding } from '../../models/interfaces/basic-funding.interface'
import { BasicItemResult } from '../../models/interfaces/basic-item-result.interface'
import { BasicNotificationsResult, NotificiationNameEnum } from '../../models/interfaces/basic-notifications.interface'
import { BasicResult } from '../../models/interfaces/basic-result.interface'
import { UserCreationBody } from '../../models/interfaces/user-creation-body.interface'
import { ConfigService } from '../../services/config.service'
import { LogService } from '../../services/log.service'

@Injectable({ providedIn: 'root' })
export class StageService {
   private _cn = this.constructor.name
   private _httpClient = inject(HttpClient)
   private _logService = inject(LogService)
   private _configService = inject(ConfigService)

   getStatuses(): Observable<BasicItemResult[]> {
      this._logService.info(this._cn, this.getStatuses.name, this._logService.START)
      return this._httpClient.get<BasicItemResult[]>(`${this._configService.apiUrl}/projects/statuses`)
   }

   getVisibilities(): Observable<BasicItemResult[]> {
      this._logService.info(this._cn, this.getVisibilities.name, this._logService.START)
      return this._httpClient.get<BasicItemResult[]>(`${this._configService.apiUrl}/projects/visibilities`)
   }

   getSectors(): Observable<BasicItemResult[]> {
      this._logService.info(this._cn, this.getSectors.name, this._logService.START)
      return this._httpClient.get<BasicItemResult[]>(`${this._configService.apiUrl}/projects/sectors`)
   }

   getTechnologies(): Observable<BasicItemResult[]> {
      this._logService.info(this._cn, this.getTechnologies.name, this._logService.START)
      return this._httpClient.get<BasicItemResult[]>(`${this._configService.apiUrl}/projects/technologies`)
   }

   getBeneficiaries(): Observable<BasicItemResult[]> {
      this._logService.info(this._cn, this.getBeneficiaries.name, this._logService.START)
      return this._httpClient.get<BasicItemResult[]>(`${this._configService.apiUrl}/beneficiaries`)
   }

   getPNOCountryResponsibles(): Observable<BasicItemResult[]> {
      this._logService.info(this._cn, this.getPNOCountryResponsibles.name, this._logService.START)
      return this._httpClient.get<BasicItemResult[]>(`${this._configService.apiUrl}/projects/countries?type=PNO_COUNTRY_RESPONSIBLES`)
   }

   getCountries(): Observable<BasicItemResult[]> {
      this._logService.info(this._cn, this.getCountries.name, this._logService.START)
      return this._httpClient.get<BasicItemResult[]>(`${this._configService.apiUrl}/projects/countries?type=PROJECT_COUNTRIES`)
   }

   getSoughtPartnerCountries(): Observable<BasicItemResult[]> {
      this._logService.info(this._cn, this.getSoughtPartnerCountries.name, this._logService.START)
      return this._httpClient.get<BasicItemResult[]>(`${this._configService.apiUrl}/projects/countries?type=SOUGHT_PARTNERS_COUNTRIES&excludeRegions=true`)
   }

   getSoughtPartnerRegions(countryId: string): Observable<BasicItemResult[]> {
      this._logService.info(this._cn, this.getSoughtPartnerRegions.name, this._logService.START)
      return this._httpClient.get<BasicItemResult[]>(`${this._configService.apiUrl}/projects/countries/${countryId}/regions?type=SOUGHT_PARTNERS_COUNTRIES`)
   }

   getRegions(countryId: string): Observable<BasicItemResult[]> {
      this._logService.info(this._cn, this.getRegions.name, this._logService.START)
      return this._httpClient.get<BasicItemResult[]>(`${this._configService.apiUrl}/projects/countries/${countryId}/regions`)
   }

   searchFundings(request: {
      researchString: string
      pageNumber: number
   }): Observable<BasicResult<BasicFunding[], { researchString: string; pageNumber: number }>> {
      this._logService.info(this._cn, this.searchFundings.name, this._logService.START)
      this._logService.info(this._cn, this.searchFundings.name, 'research funding query: ', request)

      const options = {
         params: new HttpParams()
            .set('searchTerm', request.researchString.trim())
            .set('pageNumber', 0)
            .set('pageSize', 20)
            .set('orderBy', 'deadlineProximity')
            .set('excludeDeleted', true)
            .set('excludePrivateDraft', true),
      }

      return this._httpClient.get<BasicResult<BasicFunding[], { researchString: string; pageNumber: number }>>(
         `${this._configService.apiUrl}/grants:autocomplete`,
         options
      )
   }

   searchPrograms(code: string): Observable<BasicResult<BasicItemResult[], {}>> {
      this._logService.info(this._cn, this.searchPrograms.name, this._logService.START)
      this._logService.info(this._cn, this.searchPrograms.name, 'research program query: ', code)

      const options = {
         params: new HttpParams().set('searchTerm', code.trim()).set('pageNumber', 0).set('pageSize', 20).set('orderBy', 'title'),
      }

      return this._httpClient.get<BasicResult<BasicItemResult[], {}>>(`${this._configService.old_BE_ApiUrl}/programs:autocomplete`, options)
   }

   searchCalls(title: string): Observable<BasicResult<BasicItemResult[], {}>> {
      this._logService.info(this._cn, this.searchCalls.name, this._logService.START)
      this._logService.info(this._cn, this.searchCalls.name, 'research call query: ', title)

      const options = {
         params: new HttpParams().set('searchTerm', title.trim()).set('pageNumber', 0).set('pageSize', 20).set('orderBy', 'title'),
      }

      return this._httpClient.get<BasicResult<BasicItemResult[], {}>>(`${this._configService.old_BE_ApiUrl}/calls:autocomplete`, options)
   }

   searchTags(tagName: string): Observable<BasicResult<BasicItemResult[], undefined>> {
      this._logService.info(this._cn, this.searchTags.name, this._logService.START)
      this._logService.info(this._cn, this.searchTags.name, 'research tag query: ', tagName)

      const options = {
         params: new HttpParams().set('term', tagName.trim()).set('pageNumber', 0).set('pageSize', 20),
      }
      return this._httpClient.get<BasicResult<BasicItemResult[], undefined>>(`${this._configService.apiUrl}/projects/tags:autocomplete`, options)
   }

   searchPNOPerson(request: { keywords: string; pageSize: number; page: number }): Observable<BasicResult<BasicItemResult[], undefined>> {
      this._logService.info(this._cn, this.searchPNOPerson.name, this._logService.START)
      this._logService.info(this._cn, this.searchPNOPerson.name, 'research PNO person query: ', request)
      return this._httpClient.post<BasicResult<BasicItemResult[], undefined>>(`${this._configService.apiUrl}/persons:autocomplete`, request)
   }

   getMeasuringPurposes(): Observable<BasicItemResult[]> {
      this._logService.info(this._cn, this.getMeasuringPurposes.name, this._logService.START)
      return this._httpClient.get<BasicItemResult[]>(`${this._configService.apiUrl}/projects/measuring-purposes`)
   }

   getPlatforms(): Observable<BasicItemResult[]> {
      this._logService.info(this._cn, this.getPlatforms.name, this._logService.START)
      return this._httpClient.get<BasicItemResult[]>(`${this._configService.apiUrl}/projects/platforms`)
   }

   getSoughtPartnerType(): Observable<BasicItemResult[]> {
      this._logService.info(this._cn, this.getSoughtPartnerType.name, this._logService.START)
      return this._httpClient.get<BasicItemResult[]>(`${this._configService.apiUrl}/projects/sought-partner-types`)
   }

   deleteProject(id: number, status: number): Observable<any> {
      this._logService.info(this._cn, this.deleteProject.name, this._logService.START)
      this._logService.info(this._cn, this.deleteProject.name, 'project to delete id:', id)
      this._logService.info(this._cn, this.deleteProject.name, 'project to delete status:', status)
      return this._httpClient.delete(`${this._configService.apiUrl}/projects/${id}?status=${status}`)
   }

   createUser(request: UserCreationBody): Observable<any> {
      this._logService.info(this._cn, this.createUser.name, this._logService.START)
      this._logService.info(this._cn, this.createUser.name, 'creation user body:', request)
      return this._httpClient.post(`${this._configService.apiUrl}/users`, request)
   }

   changePassword(request: { oldPassword: string; newPassword: string }): Observable<any> {
      this._logService.info(this._cn, this.changePassword.name, this._logService.START)
      return this._httpClient.post(`${this._configService.apiUrl}/users/current:change-password`, request)
   }

   updateAccountInfo(request: AccountInfoRequest): Observable<any> {
      this._logService.info(this._cn, this.updateAccountInfo.name, this._logService.START)
      this._logService.info(this._cn, this.updateAccountInfo.name, 'update account info request:', request)
      return this._httpClient.put(`${this._configService.apiUrl}/users/current`, request)
   }

   getAccountInfo(): Observable<any> {
      this._logService.info(this._cn, this.getAccountInfo.name, this._logService.START)
      return this._httpClient.get(`${this._configService.apiUrl}/users/current`)
   }

   deleteAvatar(): Observable<any> {
      this._logService.info(this._cn, this.deleteAvatar.name, this._logService.START)
      return this._httpClient.delete(`${this._configService.apiUrl}/users/current/avatar`)
   }

   getAvatar(): Observable<BasicFile> {
      this._logService.info(this._cn, this.getAvatar.name, this._logService.START)
      return this._httpClient.get<BasicFile>(`${this._configService.apiUrl}/users/current/avatar`)
   }

   updateAvatar(file: BasicFile): Observable<any> {
      this._logService.info(this._cn, this.updateAvatar.name, this._logService.START)
      this._logService.info(this._cn, this.updateAvatar.name, 'update avatar request:', file)
      return this._httpClient.post(`${this._configService.apiUrl}/users/current/avatar`, file)
   }

   resetPassword(request: { emailAddress: string }): Observable<any> {
      this._logService.info(this._cn, this.resetPassword.name, this._logService.START)
      this._logService.info(this._cn, this.resetPassword.name, 'reset password request:', request)
      return this._httpClient.post(`${this._configService.apiUrl}/passwords/tokens`, request)
   }

   // public service
   requestResetPassword(pwd: string, token: string | null): Observable<any> {
      this._logService.info(this._cn, this.requestResetPassword.name, this._logService.START)
      this._logService.info(this._cn, this.requestResetPassword.name, 'reset password request:', { pwd, token })
      return this._httpClient.post(`${this._configService.publicApiUrl}/passwords/tokens/${token}`, { password: pwd })
   }

   // public service
   requestActivateAccount(token: string | null): Observable<any> {
      this._logService.info(this._cn, this.requestActivateAccount.name, this._logService.START)
      this._logService.info(this._cn, this.requestActivateAccount.name, 'activate account request:', { token })
      return this._httpClient.post(`${this._configService.publicApiUrl}/users/verify/tokens/${token}`, {})
   }

   getPNOUserNotifications(): Observable<BasicNotificationsResult> {
      this._logService.info(this._cn, this.getPNOUserNotifications.name, this._logService.START)
      let toolName = 'PMP_CLOUD_TOOL'
      return this._httpClient.get<BasicNotificationsResult>(`${this._configService.old_BE_ApiUrl}/preferences/notifications?tool=${toolName}`)
   }

   updatePNOUserNotifications(notificationName: NotificiationNameEnum, isActive: boolean): Observable<any> {
      this._logService.info(this._cn, this.updatePNOUserNotifications.name, this._logService.START)
      let toolName = 'PMP_CLOUD_TOOL'
      let requestBody = {
         enabled: isActive,
      }
      return this._httpClient.put(`${this._configService.old_BE_ApiUrl}/preferences/notifications/${toolName}/${notificationName}`, requestBody)
   }
}
